// Stroke point — the only structurally-shared payload between
// `stroke.segment` (client → server) and `stroke.segment` broadcast (server → client).
//
// (protocol.md §4.3 — 「{ x, y, p?, pAlpha? }」)
import { z } from 'zod';
/**
 * One coordinate of a stroke.
 *
 * Pressure is carried on **two independent axes** so that a single pen can
 * react to width-pressure and alpha-pressure separately (consult 2026-05-09):
 *
 * - `p`       — width-axis effective pressure (0..1, optional). Backward-
 *               compatible with the original single-pressure protocol;
 *               receivers that ignore `pAlpha` keep working as before.
 * - `pAlpha`  — alpha-axis effective pressure (0..1, optional). Sent only
 *               when the sender computes a separate alpha pressure (e.g.
 *               `'brush'` with both width-pressure and alpha-pressure ON).
 *
 * Why two fields instead of one: with a single `p` value the receiver's
 * `computeEffectivePressure` had to pick a single axis to honor when both
 * width-pressure and alpha-pressure were enabled. The width-axis ended up
 * dominating, which silently disabled the alpha-axis `min` slider for
 * `'brush'`. Carrying the two effective pressures independently restores the
 * old Java版 `JTabletConfigCanvas` behavior (太さ独立 + 濃さ独立 min/max
 * sliders) and matches the CSP / Photoshop convention of per-effect-axis
 * pressure configuration.
 *
 * On the wire, an unset `pAlpha` is omitted entirely (Zod `optional()` +
 * `JSON.stringify` drops `undefined`), so legacy clients/servers that don't
 * know about the field continue to round-trip without change. Receivers
 * that consume `pAlpha` should fall back to `pt.pAlpha ?? pt.p ?? 1.0`
 * when computing `alphaScale` (= drawing-core, Step 5b).
 *
 * Both fields are independently omitted on devices without pressure support.
 */
export const strokePointSchema = z.object({
    x: z.number(),
    y: z.number(),
    p: z.number().min(0).max(1).optional(),
    pAlpha: z.number().min(0).max(1).optional(),
});
//# sourceMappingURL=point.js.map